import React, {useEffect, useMemo, useRef} from "react";
import {CountUp} from "countup.js";

import {useSelector} from "react-redux";
import {RootState} from "../../../../store/store";
import {ProductionBox} from "./ProductionBox";
import {EconomicImpactBox} from "./EconomicImpactBox";
import {EmploymentImpactBox} from "./EmploymentImpactBox";
import {CommunityImpactBox} from "./CommunityImpactBox";

import {formatValue, getCalculations,} from "../../../../utils/boxes/calculations";

import {StyledItemBox} from "../../../../components/styles/StyledItemBox";
import {CURRENCY, isRegion} from "../../../../config";
import {LATAM_COUNTRY_NAMES} from "../../../../features/countries/types";

export const ImpactBoxes: React.FC = () => {
  const {
    statistics: {statisticsForSelectedCountry},
    map: {selectedCountry, selectedDistrict},
  } = useSelector((state: RootState) => state);

  const facilitiesRef = useRef<HTMLHeadingElement | null>(null);
  const unitCasesProducedRef = useRef<HTMLHeadingElement | null>(null);
  const litersProducedRef = useRef<HTMLHeadingElement | null>(null);
  const consumerRetailValueRef = useRef<HTMLHeadingElement | null>(null);
  const spentOnDomesticSuppliersRef = useRef<HTMLHeadingElement | null>(null);

  const valueAddedToTheLocalEconomyRef = useRef<HTMLHeadingElement | null>(
    null
  );
  const gdpContributionRef = useRef<HTMLHeadingElement | null>(null);
  const taxRevenueContributionRef = useRef<HTMLHeadingElement | null>(null);
  const valueAddedRef = useRef<HTMLHeadingElement | null>(null);

  const communityInvestedRef = useRef<HTMLHeadingElement | null>(null);

  const directJobsSupportedRef = useRef<HTMLHeadingElement | null>(null);
  const indirectJobsSupportedRef = useRef<HTMLHeadingElement | null>(null);
  const directToIndirectPrefixRef = useRef<HTMLHeadingElement | null>(null);
  const directToIndirectSuffixRef = useRef<HTMLHeadingElement | null>(null);

  const countUpItems = useMemo(
    () => [
      {
        ref: facilitiesRef,
        value: formatValue(
          statisticsForSelectedCountry?.data_points.country_impact
            .facilities_value || 0
        ),
        options: getCalculations(
          statisticsForSelectedCountry?.data_points.country_impact
            .facilities_value || 0,
          "",
          0,
          selectedCountry?.country_name === LATAM_COUNTRY_NAMES.CARICOM
        ),
      },
      {
        ref: isRegion.latam ? unitCasesProducedRef : litersProducedRef,
        value: formatValue(
          (isRegion.latam
            ? statisticsForSelectedCountry?.data_points.country_impact.uc_value
            : statisticsForSelectedCountry?.data_points.country_impact
              .liter_value) || 0
        ),
        options: getCalculations(
          (isRegion.latam
            ? statisticsForSelectedCountry?.data_points.country_impact.uc_value
            : statisticsForSelectedCountry?.data_points.country_impact
              .liter_value) || 0,
          isRegion.latam
            ? statisticsForSelectedCountry?.data_points.country_impact.uc_unit
            : statisticsForSelectedCountry?.data_points.country_impact
              .liter_unit,
          1
        ),
      },
      {
        ref: consumerRetailValueRef,
        value: formatValue(
          statisticsForSelectedCountry?.data_points.country_impact
            .consumer_retail_value || 0
        ),
        options: getCalculations(
          statisticsForSelectedCountry?.data_points.country_impact
            .consumer_retail_value || 0,
          statisticsForSelectedCountry?.data_points.country_impact
            .consumer_retail_unit,
          1
        ),
      },
      {
        ref: spentOnDomesticSuppliersRef,
        value: formatValue(
          statisticsForSelectedCountry?.data_points.country_impact
            .domestic_suppliers_value || 0
        ),
        options: getCalculations(
          statisticsForSelectedCountry?.data_points.country_impact
            .domestic_suppliers_value || 0,
          statisticsForSelectedCountry?.data_points.country_impact
            .domestic_suppliers_unit,
          1
        ),
      },
      {
        ref: valueAddedToTheLocalEconomyRef,
        value: formatValue(
          statisticsForSelectedCountry?.data_points.economic_impact
            .value_added || 0
        ),
        options: getCalculations(
          statisticsForSelectedCountry?.data_points.economic_impact
            .value_added || 0,
          statisticsForSelectedCountry?.data_points.economic_impact
            .value_added_unit,
          1
        ),
      },
      {
        ref: gdpContributionRef,
        value: formatValue(
          statisticsForSelectedCountry?.data_points.economic_impact
            .gdp_contribution_value || 0
        ),
        options: getCalculations(
          statisticsForSelectedCountry?.data_points.economic_impact
            .gdp_contribution_value || 0,
          statisticsForSelectedCountry?.data_points.economic_impact
            .gdp_contribution_value,
          isRegion.usa ? 2 : 1
        ),
      },
      {
        ref: taxRevenueContributionRef,
        value: formatValue(
          statisticsForSelectedCountry?.data_points.economic_impact.tax_value ||
          0
        ),
        options: getCalculations(
          statisticsForSelectedCountry?.data_points.economic_impact.tax_value ||
          0,
          statisticsForSelectedCountry?.data_points.economic_impact.tax_value_unit ||
          statisticsForSelectedCountry?.data_points.economic_impact.tax_value,
          isRegion.usa ? 2 : 1
        ),
      },
      {
        ref: valueAddedRef,
        value: formatValue(
          statisticsForSelectedCountry?.data_points.economic_impact
            .per_dollar_value || 0
        ),
        options: getCalculations(
          statisticsForSelectedCountry?.data_points.economic_impact
            .per_dollar_value || 0,
          CURRENCY,
          2
        ),
      },
      {
        ref: communityInvestedRef,
        value: formatValue(
          statisticsForSelectedCountry?.data_points.community_impact
            ?.total_invested || 0
        ),
        options: getCalculations(
          statisticsForSelectedCountry?.data_points.community_impact
            ?.total_invested || 0,
          statisticsForSelectedCountry?.data_points.community_impact
            ?.total_invested_unit,
          1
        ),
      },
      {
        ref: directJobsSupportedRef,
        value: formatValue(
          statisticsForSelectedCountry?.data_points.employment_impact
            .direct_jobs_value || 0
        ),
      },
      {
        ref: indirectJobsSupportedRef,
        value: formatValue(
          statisticsForSelectedCountry?.data_points.employment_impact
            .indirect_jobs_value || 0
        ),
      },
      {
        ref: directToIndirectPrefixRef,
        value: 1,
      },
      {
        ref: directToIndirectSuffixRef,
        value: formatValue(
          statisticsForSelectedCountry?.data_points.employment_impact
            .jobs_multiplier_value || 0
        ),
      },
    ],
    [statisticsForSelectedCountry, selectedCountry]
  );

  useEffect(() => {
    if (!statisticsForSelectedCountry) return;

    const timeoutId = setTimeout(() => {
      countUpItems.forEach(({ref, value, options}) => {
        if (ref.current) {
          const countUp = new CountUp(ref.current, value, options);
          if (!countUp.error) {
            countUp.start();
          }
        }
      });
    }, 100);

    return () => clearTimeout(timeoutId);
  }, [countUpItems, statisticsForSelectedCountry]);

  const showCommunity = isRegion.usa && !selectedDistrict;

  return (
    <>
      <div className={`box_container${!showCommunity && isRegion.usa ? ' no-community' : ''}`}>
        <StyledItemBox className="paper_box_1">
          <ProductionBox
            consumerRetailValueRef={consumerRetailValueRef}
            facilitiesRef={facilitiesRef}
            spentOnDomesticSuppliersRef={spentOnDomesticSuppliersRef}
            unitCasesProducedRef={unitCasesProducedRef}
            litersProducedRef={litersProducedRef}
            hideBottlingPartners={isRegion.usa && !!selectedDistrict}
          />
        </StyledItemBox>
        <StyledItemBox className="paper_box_2">
          <EconomicImpactBox
            gdpContributionRef={gdpContributionRef}
            taxRevenueContributionRef={taxRevenueContributionRef}
            valueAddedToTheLocalEconomyRef={valueAddedToTheLocalEconomyRef}
            valueAddedRef={valueAddedRef}
          />
        </StyledItemBox>
        <StyledItemBox className="paper_box_3">
          <EmploymentImpactBox
            directToIndirectSuffixRef={directToIndirectSuffixRef}
            directJobsSupportedRef={directJobsSupportedRef}
            directToIndirectPrefixRef={directToIndirectPrefixRef}
            indirectJobsSupportedRef={indirectJobsSupportedRef}
          />
        </StyledItemBox>
        {showCommunity && (
          <StyledItemBox className="paper_box_4">
            <CommunityImpactBox
              communityInvestedRef={communityInvestedRef}
            />
          </StyledItemBox>
        )}
      </div>
    </>
  );
};
